import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import {
  Megaphone, Plus, RefreshCw, Search, Download, Tag, X,
  Send, CheckCircle2, XCircle, Sun, Moon, ListFilter, Calendar,
} from 'lucide-react';
import { useTheme, useFeatureFlags } from '../components/Navbar';
import useAutoLogout from '../hooks/useAutoLogout';
import CampaignModal from '../components/CampaignModal';
import Toast from '../components/Toast';
import API_BASE from '../config';

/* ---- shared theme tokens (same system as Dashboard) ---- */
const TK = {
  light: {
    pageBg:'#F5F6FA', card:'#FFFFFF', border:'#ECEEF2',
    text:'#0F1623', sub:'#6B7280', muted:'#9CA3AF',
    accent:'#5B4EE8', accentBg:'#EEF0FD', accentTxt:'#4338CA',
    row:'#FAFBFC', rowBdr:'#F0F2F5', input:'#FFFFFF',
    btnPri:'#5B4EE8', btnPriH:'#4C3FD4', btnPriTxt:'#fff',
    btnSec:'#fff', btnSecTxt:'#374151', btnSecBdr:'#ECEEF2',
    chip:'#F3F4F6', chipTxt:'#374151', overlay:'rgba(15,22,35,0.32)',
    toggleBg:'#ECEEF2', toggleActive:'#fff',
    errBg:'#FEF2F2', errBdr:'#FECACA', errTxt:'#DC2626',
  },
  dark: {
    pageBg:'#0D0D14', card:'#131320', border:'#1E1E2E',
    text:'#F1F2F6', sub:'#8B8FA8', muted:'#4B5263',
    accent:'#7C72FF', accentBg:'rgba(124,114,255,0.12)', accentTxt:'#A5A0FF',
    row:'#18182A', rowBdr:'#1E1E2E', input:'#18182A',
    btnPri:'#6C63FF', btnPriH:'#5B53E8', btnPriTxt:'#fff',
    btnSec:'#1A1A2A', btnSecTxt:'#D1D5DB', btnSecBdr:'#1E1E2E',
    chip:'#1A1A2A', chipTxt:'#C7CBDA', overlay:'rgba(0,0,0,0.55)',
    toggleBg:'#1A1A2A', toggleActive:'#1E1E2E',
    errBg:'rgba(220,38,38,0.12)', errBdr:'rgba(220,38,38,0.3)', errTxt:'#F87171',
  },
};

const TABS = ['All', 'WhatsApp', 'Email', 'SMS'];
const TAB_FLAG_MAP = { WhatsApp: 'whatsapp_campaigns', Email: 'email_journeys', SMS: 'sms_channel' };
const STATUS_BUCKETS = ['All', 'Completed', 'In Process', 'Scheduled', 'Pending'];
const CAT_COLOR = { Marketing:'#EC4899', Transactional:'#3B82F6', General:'#6B7280' };
const LBL_KEY = 'martech_campaign_labels';

const getToken = () =>
  localStorage.getItem('martech_token') || localStorage.getItem('token') ||
  sessionStorage.getItem('martech_token') || sessionStorage.getItem('token') || '';

const fmtNum = (n) => n==null?'0':Number(n).toLocaleString('en-IN');

function istParts(dateStr) {
  if (!dateStr) return null;
  const d = new Date(dateStr);
  if (isNaN(d)) return null;
  const ist = new Date(d.getTime() + d.getTimezoneOffset()*60000 + 5.5*3600000);
  return ist;
}
function toIST(dateStr) {
  const ist = istParts(dateStr);
  if (!ist) return '—';
  return ist.toLocaleString('en-IN', { day:'2-digit', month:'short', year:'numeric', hour:'2-digit', minute:'2-digit', hour12:true });
}
function istDateKey(dateStr) {
  const ist = istParts(dateStr);
  if (!ist) return '';
  const y=ist.getFullYear(), m=String(ist.getMonth()+1).padStart(2,'0'), d=String(ist.getDate()).padStart(2,'0');
  return `${y}-${m}-${d}`;
}

const CAT_RULES = [
  { cat:'Transactional', kws:['appointment','reminder','otp','confirm','receipt','invoice','welcome','followup','follow_up','booking','verify'] },
  { cat:'Marketing',     kws:['offer','sale','discount','birthday','win_back','winback','promo','festive','deal','launch'] },
];
function categorize(name='', channel='') {
  const s = `${name} ${channel}`.toLowerCase();
  for (const r of CAT_RULES) if (r.kws.some(k => s.includes(k))) return r.cat;
  return 'General';
}

/* ---- status bucket mapper: collapses raw statuses into the 4 user-facing buckets ---- */
function statusBucket(raw) {
  const s = (raw || '').toString().toLowerCase().trim();
  if (['sent','delivered','opened','clicked','completed','done','success'].includes(s)) return 'Completed';
  if (['sending','processing','running','in_progress','in process','active','queued'].includes(s)) return 'In Process';
  if (s === 'scheduled') return 'Scheduled';
  return 'Pending'; // pending, dry_run, failed, bounced, unknown — never hidden
}

const loadLabels = () => { try { return JSON.parse(localStorage.getItem(LBL_KEY) || '{}'); } catch { return {}; } };
const saveLabels = (m) => { try { localStorage.setItem(LBL_KEY, JSON.stringify(m)); } catch {} };

function channelPill(ch) {
  const c = (ch || '').toLowerCase();
  if (c === 'whatsapp') return { bg:'rgba(37,211,102,0.14)',  color:'#16a34a' };
  if (c === 'email')    return { bg:'rgba(59,130,246,0.14)',  color:'#2563eb' };
  if (c === 'sms')      return { bg:'rgba(245,158,11,0.16)',  color:'#b45309' };
  if (c === 'rcs')      return { bg:'rgba(139,92,246,0.14)',  color:'#7c3aed' };
  return { bg:'rgba(107,114,128,0.16)', color:'#6b7280' };
}
function statusPill(st) {
  const s = (st || '').toLowerCase();
  const m = {
    pending:['rgba(245,158,11,0.16)','#b45309'], sent:['rgba(59,130,246,0.14)','#2563eb'],
    delivered:['rgba(16,185,129,0.16)','#059669'], opened:['rgba(124,58,237,0.14)','#7c3aed'],
    clicked:['rgba(245,158,11,0.16)','#b45309'], failed:['rgba(239,68,68,0.14)','#dc2626'],
    bounced:['rgba(239,68,68,0.14)','#dc2626'], scheduled:['rgba(124,58,237,0.14)','#7c3aed'],
    dry_run:['rgba(107,114,128,0.16)','#6b7280'],
  };
  const [bg,color] = m[s] || ['rgba(107,114,128,0.16)','#6b7280'];
  return { bg, color };
}

function exportCSV(list, suffix='') {
  const headers = ['Name','Category','Labels','Channel','Status','Sent','Opened %','Clicked %','Scheduled (IST)'];
  const esc = (v) => `"${String(v ?? '').replace(/"/g,'""')}"`;
  const lines = [headers.join(',')];
  list.forEach(r => lines.push([
    (r.name||'').replace(/_/g,' '), r.category, (r.labels||[]).join('; '),
    r.channel, (r.status||'').replace(/_/g,' '), r.sent, r.opened, r.clicked, toIST(r.date),
  ].map(esc).join(',')));
  const blob = new Blob(['\uFEFF' + lines.join('\n')], { type:'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = `martech-campaigns${suffix}-${new Date().toISOString().slice(0,10)}.csv`;
  document.body.appendChild(a); a.click(); document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

function ThemeToggle({ tk }) {
  const { theme, setTheme } = useTheme();
  const btn = (t, Icon, label) => (
    <button type="button" key={t} onClick={()=>setTheme(t)} style={{display:'flex',alignItems:'center',gap:5,background:theme===t?tk.toggleActive:'transparent',border:'none',borderRadius:6,padding:'5px 10px',cursor:'pointer',fontSize:12,fontWeight:500,color:theme===t?tk.text:tk.sub,boxShadow:theme===t?'0 1px 3px rgba(0,0,0,0.08)':'none',transition:'all 0.15s',whiteSpace:'nowrap'}}>
      <Icon size={13}/>{label}
    </button>
  );
  return <div style={{display:'flex',background:tk.toggleBg,borderRadius:8,padding:3}}>{btn('light',Sun,'Light')}{btn('dark',Moon,'Dark')}</div>;
}

function Stat({ Icon, label, value, accent, loading, tk }) {
  return (
    <div style={{background:tk.card,border:`1px solid ${tk.border}`,borderRadius:12,padding:'14px 16px',display:'flex',flexDirection:'column',gap:10}}>
      <div style={{width:34,height:34,borderRadius:9,background:accent+'18',display:'flex',alignItems:'center',justifyContent:'center'}}>
        <Icon size={17} color={accent} strokeWidth={2}/>
      </div>
      <div>
        <div style={{fontSize:28,fontWeight:700,color:tk.text,letterSpacing:'-0.5px',lineHeight:1}}>{loading?'—':fmtNum(value)}</div>
        <div style={{fontSize:10,fontWeight:700,color:tk.muted,textTransform:'uppercase',letterSpacing:'0.05em',marginTop:6}}>{label}</div>
      </div>
    </div>
  );
}

export default function Campaigns() {
  const navigate = useNavigate();
  useAutoLogout();
  const { theme } = useTheme();
  const tk = TK[theme];
  const { flags } = useFeatureFlags();
  const visibleTabs = TABS.filter(t => t === 'All' || flags[TAB_FLAG_MAP[t]] !== false);
  const [searchParams] = useSearchParams();

  const [rows,       setRows]       = useState([]);
  const [labelMap,   setLabelMap]   = useState(loadLabels);
  const [loading,    setLoading]    = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [showModal,  setShowModal]  = useState(false);
  const location = useLocation();
  useEffect(() => {
    if (location.state && location.state.openNew) {
      setShowModal(true);
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);
  const [toast,      setToast]      = useState(null);

  // filters
  const [activeTab, setActiveTab] = useState(() => {
    const p = (searchParams.get('tab') || '').toLowerCase();
    return TABS.find(t => t.toLowerCase() === p) || 'All';
  });
  const [q,        setQ]        = useState('');
  const [statusF,  setStatusF]  = useState('All');   // now holds a bucket name
  const [catF,     setCatF]     = useState('All');
  const [labelF,   setLabelF]   = useState('All');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo,   setDateTo]   = useState('');

  // inline label editor + selected campaign drawer
  const [editId, setEditId] = useState(null);
  const [draft,  setDraft]  = useState('');
  const [selectedId, setSelectedId] = useState(null);

  const showToast = (msg, variant='green') => { setToast({ msg, variant }); setTimeout(()=>setToast(null), 3100); };

  const fetchData = useCallback((isRefresh=false) => {
    const token = getToken();
    if (!token) { navigate('/login'); return; }
    const h = { Authorization:`Bearer ${token}`, 'Content-Type':'application/json' };
    if (isRefresh) setRefreshing(true); else setLoading(true);

    Promise.allSettled([
      fetch(`${API_BASE}/api/v1/campaign/schedule/list`, { headers:h }).then(r=>r.json()),
      fetch(`${API_BASE}/api/v1/email/events`,           { headers:h }).then(r=>r.json()),
    ]).then(([campRes, emailRes]) => {
      const scheduled = campRes.status==='fulfilled' && campRes.value.success
        ? (campRes.value.scheduled || campRes.value.data || campRes.value.campaigns || []) : [];
      const emailEvents = emailRes.status==='fulfilled' && emailRes.value.success
        ? (emailRes.value.events || []) : [];

      setRows([
        ...scheduled.map(r => ({
          id:      String(r.schedule_id || r.id || `s_${r.scheduled_at}_${r.segment_name}`),
          name:    r.segment_name || r.name || '—',
          channel: (r.channel || 'whatsapp').toLowerCase(),
          status:  r.status || 'pending',
          sent:    r.sent_count != null ? String(r.sent_count) : '—',
          opened:  r.open_rate  != null ? `${r.open_rate}%`  : '—',
          clicked: r.click_rate != null ? `${r.click_rate}%` : '—',
          date:    r.scheduled_at,
        })),
        ...emailEvents.map(r => ({
          id:      String(r.campaign_event_id || r.id || `e_${r.sent_at}_${r.campaign_name}`),
          name:    r.campaign_name || '—',
          channel: (r.channel || 'email').toLowerCase(),
          status:  r.status || 'sent',
          sent:    '1', opened:'—', clicked:'—',
          date:    r.sent_at,
        })),
      ]);
    }).finally(() => { setLoading(false); setRefreshing(false); });
  }, [navigate]);

  useEffect(() => { fetchData(false); }, [fetchData]);

  /* labels */
  const addLabel = (id, raw) => {
    const t = (raw||'').trim(); if (!t) return;
    setLabelMap(prev => {
      const cur = prev[id] || [];
      if (cur.includes(t)) return prev;
      const next = { ...prev, [id]: [...cur, t] };
      saveLabels(next); return next;
    });
    setDraft('');
  };
  const removeLabel = (id, t) => {
    setLabelMap(prev => {
      const next = { ...prev, [id]: (prev[id]||[]).filter(x=>x!==t) };
      if (!next[id]?.length) delete next[id];
      saveLabels(next); return next;
    });
  };

  /* pipeline */
  const enriched = useMemo(() => rows.map(r => ({
    ...r, category: categorize(r.name, r.channel), labels: labelMap[r.id] || [],
  })), [rows, labelMap]);

  const selected = useMemo(() => enriched.find(r => r.id === selectedId) || null, [enriched, selectedId]);

  const stats = useMemo(() => {
    const n = s => (s||'').toLowerCase();
    return {
      total:     enriched.length,
      sent:      enriched.filter(r=>['sent','delivered','opened','clicked'].includes(n(r.status))).length,
      delivered: enriched.filter(r=>['delivered','opened','clicked'].includes(n(r.status))).length,
      failed:    enriched.filter(r=>['failed','bounced'].includes(n(r.status))).length,
    };
  }, [enriched]);

  const tabCounts = useMemo(() => ({
    All: enriched.length,
    WhatsApp: enriched.filter(r=>r.channel==='whatsapp').length,
    Email: enriched.filter(r=>r.channel==='email').length,
    SMS: enriched.filter(r=>r.channel==='sms').length,
  }), [enriched]);

  const statusCounts = useMemo(() => {
    const c = { All: enriched.length, Completed:0, 'In Process':0, Scheduled:0, Pending:0 };
    enriched.forEach(r => { const b = statusBucket(r.status); c[b] = (c[b]||0) + 1; });
    return c;
  }, [enriched]);

  const base = useMemo(() => enriched.filter(r => {
    if (activeTab !== 'All' && r.channel !== activeTab.toLowerCase()) return false;
    if (statusF !== 'All' && statusBucket(r.status) !== statusF) return false;
    if (q.trim() && !(r.name||'').toLowerCase().includes(q.trim().toLowerCase())) return false;
    const k = istDateKey(r.date);
    if (dateFrom && (!k || k < dateFrom)) return false;
    if (dateTo   && (!k || k > dateTo))   return false;
    return true;
  }), [enriched, activeTab, statusF, q, dateFrom, dateTo]);

  const catCounts = useMemo(() => base.reduce((a,r)=>{ a[r.category]=(a[r.category]||0)+1; return a; }, {}), [base]);
  const afterCat  = useMemo(() => catF==='All' ? base : base.filter(r=>r.category===catF), [base, catF]);
  const labelCounts = useMemo(() => {
    const c = {}; afterCat.forEach(r => r.labels.forEach(l => { c[l]=(c[l]||0)+1; })); return c;
  }, [afterCat]);
  const visible = useMemo(() => labelF==='All' ? afterCat : afterCat.filter(r=>r.labels.includes(labelF)), [afterCat, labelF]);

  const anyFilter = activeTab!=='All' || statusF!=='All' || catF!=='All' || labelF!=='All' || q.trim() || dateFrom || dateTo;
  const clearAll = () => { setActiveTab('All'); setStatusF('All'); setCatF('All'); setLabelF('All'); setQ(''); setDateFrom(''); setDateTo(''); };

  const selStyle = { background:tk.input, color:tk.text, border:`1px solid ${tk.border}`, borderRadius:8, padding:'8px 10px', fontSize:13, fontWeight:500, cursor:'pointer', outline:'none' };
  const dateStyle = { ...selStyle, colorScheme: theme };

  /* label cell shared by table + cards + drawer */
  const LabelCell = ({ row }) => (
    <div style={{display:'flex',flexWrap:'wrap',gap:4,alignItems:'center'}}>
      {row.labels.map(l => (
        <span key={l} style={{display:'inline-flex',alignItems:'center',gap:3,background:tk.chip,color:tk.chipTxt,fontSize:10,fontWeight:600,padding:'2px 6px',borderRadius:6}}>
          {l}<X size={10} style={{cursor:'pointer'}} onClick={e=>{ e.stopPropagation(); removeLabel(row.id,l); }}/>
        </span>
      ))}
      {editId===row.id ? (
        <input
          autoFocus value={draft}
          onClick={e=>e.stopPropagation()}
          onChange={e=>setDraft(e.target.value)}
          onKeyDown={e=>{ if(e.key==='Enter'){ addLabel(row.id,draft); setEditId(null); } if(e.key==='Escape'){ setEditId(null); setDraft(''); } }}
          onBlur={()=>{ addLabel(row.id,draft); setEditId(null); }}
          placeholder="label…"
          style={{width:84,background:tk.input,color:tk.text,border:`1px solid ${tk.accent}`,borderRadius:6,padding:'2px 6px',fontSize:11,outline:'none'}}
        />
      ) : (
        <button type="button" onClick={e=>{ e.stopPropagation(); setEditId(row.id); setDraft(''); }} title="Add label"
          style={{display:'inline-flex',alignItems:'center',gap:2,background:'transparent',color:tk.muted,border:`1px dashed ${tk.border}`,borderRadius:6,padding:'2px 6px',fontSize:10,fontWeight:600,cursor:'pointer'}}>
          <Tag size={10}/> Label
        </button>
      )}
    </div>
  );

  const th = { textAlign:'left', padding:'8px 12px', fontSize:10, fontWeight:700, color:tk.muted, textTransform:'uppercase', letterSpacing:'0.06em', borderBottom:`1px solid ${tk.border}`, whiteSpace:'nowrap' };
  const td = { padding:'11px 12px', borderBottom:`1px solid ${tk.rowBdr}`, fontSize:12.5, color:tk.text, verticalAlign:'top' };

  return (
    <>
      <style>{`
        .mtc-wrap{padding:20px 24px;box-sizing:border-box;min-height:100vh}
        .mtc-stats{display:grid;grid-template-columns:repeat(4,1fr);gap:12px;margin-bottom:18px}
        .mtc-hdr{display:flex;justify-content:space-between;align-items:flex-start;gap:12px;margin-bottom:18px}
        .mtc-hdr-r{display:flex;align-items:center;gap:8px;flex-wrap:wrap}
        .mtc-filters{display:flex;align-items:center;gap:10px;flex-wrap:wrap;margin-bottom:14px}
        .mtc-search{position:relative;flex:1;min-width:220px}
        .mtc-tabs{display:flex;gap:8px;flex-wrap:wrap;margin-bottom:12px}
        .mtc-chips{display:flex;gap:8px;flex-wrap:wrap;align-items:center}
        .mtc-desktop{display:block}
        .mtc-mobile{display:none}
        .mtc-tbl{width:100%;border-collapse:collapse}
        .mtc-drawer{width:min(440px,100vw)}
        @media(max-width:1024px){.mtc-stats{grid-template-columns:repeat(2,1fr)}}
        @media(max-width:640px){
          .mtc-wrap{padding:72px 16px 18px}
          .mtc-stats{grid-template-columns:repeat(2,1fr);gap:8px;margin-bottom:14px}
          .mtc-hdr{flex-direction:column;gap:10px}
          .mtc-hdr-r{width:100%;justify-content:space-between}
          .mtc-search{min-width:0;width:100%;flex:1 1 100%}
          .mtc-desktop{display:none}
          .mtc-mobile{display:flex;flex-direction:column;gap:10px}
          .mtc-drawer{width:100vw}
        }
      `}</style>

      {showModal && (
        <CampaignModal onClose={()=>setShowModal(false)}
          onSuccess={(msg,variant)=>{ showToast(msg,variant); fetchData(true); }} />
      )}
      {toast && <Toast message={toast.msg} variant={toast.variant} onDismiss={()=>setToast(null)} />}

      {/* Stats drawer */}
      {selected && (
        <div onClick={()=>setSelectedId(null)}
          style={{position:'fixed',inset:0,background:tk.overlay,zIndex:60,display:'flex',justifyContent:'flex-end'}}>
          <div className="mtc-drawer" onClick={e=>e.stopPropagation()}
            style={{background:tk.card,height:'100%',overflowY:'auto',padding:'20px 22px',borderLeft:`1px solid ${tk.border}`,boxSizing:'border-box'}}>
            <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',gap:10,marginBottom:14}}>
              <div>
                <div style={{fontSize:10,fontWeight:700,color:tk.muted,textTransform:'uppercase',letterSpacing:'0.06em',marginBottom:4}}>Campaign Stats</div>
                <h2 style={{margin:0,fontSize:19,fontWeight:700,color:tk.text,letterSpacing:'-0.3px'}}>{(selected.name||'').replace(/_/g,' ')}</h2>
              </div>
              <button type="button" onClick={()=>setSelectedId(null)} style={{background:tk.btnSec,border:`1px solid ${tk.btnSecBdr}`,borderRadius:8,width:32,height:32,display:'flex',alignItems:'center',justifyContent:'center',cursor:'pointer',color:tk.sub,flexShrink:0}}>
                <X size={16}/>
              </button>
            </div>

            <div style={{display:'flex',gap:8,flexWrap:'wrap',marginBottom:18}}>
              {(() => { const cp=channelPill(selected.channel); return <span style={{background:cp.bg,color:cp.color,fontSize:11,fontWeight:700,padding:'3px 10px',borderRadius:20,textTransform:'capitalize'}}>{selected.channel}</span>; })()}
              {(() => { const sp=statusPill(selected.status); return <span style={{background:sp.bg,color:sp.color,fontSize:11,fontWeight:700,padding:'3px 10px',borderRadius:20,textTransform:'capitalize'}}>{(selected.status||'').replace(/_/g,' ')}</span>; })()}
              <span style={{display:'inline-flex',alignItems:'center',gap:5,fontSize:11,fontWeight:600,color:tk.sub,padding:'3px 10px',border:`1px solid ${tk.border}`,borderRadius:20}}>
                <span style={{width:7,height:7,borderRadius:'50%',background:CAT_COLOR[selected.category]}}/>{selected.category}
              </span>
            </div>

            <div style={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:10,marginBottom:18}}>
              {[['Sent',selected.sent,'#3B82F6'],['Opened',selected.opened,'#8B5CF6'],['Clicked',selected.clicked,'#F59E0B']].map(([k,v,c])=>(
                <div key={k} style={{background:tk.row,border:`1px solid ${tk.border}`,borderRadius:10,padding:'12px 14px'}}>
                  <div style={{fontSize:22,fontWeight:700,color:tk.text,lineHeight:1}}>{v}</div>
                  <div style={{fontSize:10,fontWeight:700,color:c,textTransform:'uppercase',letterSpacing:'0.05em',marginTop:6}}>{k}</div>
                </div>
              ))}
            </div>

            <div style={{borderTop:`1px solid ${tk.border}`,paddingTop:14,marginBottom:18}}>
              {[['Scheduled (IST)',toIST(selected.date)],['Channel',selected.channel],['Category',selected.category],['Status',(selected.status||'').replace(/_/g,' ')]].map(([k,v])=>(
                <div key={k} style={{display:'flex',justifyContent:'space-between',padding:'7px 0',fontSize:13}}>
                  <span style={{color:tk.muted}}>{k}</span>
                  <span style={{color:tk.text,fontWeight:600,textTransform:'capitalize',textAlign:'right'}}>{v}</span>
                </div>
              ))}
            </div>

            <div style={{marginBottom:18}}>
              <div style={{fontSize:10,fontWeight:700,color:tk.muted,textTransform:'uppercase',letterSpacing:'0.06em',marginBottom:8}}>Labels</div>
              <LabelCell row={selected}/>
            </div>

            <button type="button" onClick={()=>exportCSV([selected],'-single')}
              style={{display:'inline-flex',alignItems:'center',gap:6,background:tk.btnSec,color:tk.btnSecTxt,border:`1px solid ${tk.btnSecBdr}`,borderRadius:8,padding:'9px 14px',fontSize:13,fontWeight:600,cursor:'pointer'}}>
              <Download size={14}/> Export this campaign
            </button>
          </div>
        </div>
      )}

      <div className="mtc-wrap" style={{background:tk.pageBg}}>

        {/* Header */}
        <div className="mtc-hdr">
          <div>
            <h1 style={{margin:0,fontSize:22,fontWeight:700,color:tk.text,letterSpacing:'-0.3px'}}>Campaigns</h1>
            <p style={{margin:'4px 0 0',fontSize:13,color:tk.sub}}>{loading?'—':`${visible.length} of ${enriched.length} shown`}</p>
          </div>
          <div className="mtc-hdr-r">
            <ThemeToggle tk={tk}/>
            <button type="button" onClick={()=>exportCSV(visible)} disabled={loading||!visible.length}
              style={{display:'inline-flex',alignItems:'center',gap:6,background:tk.btnSec,color:tk.btnSecTxt,border:`1px solid ${tk.btnSecBdr}`,borderRadius:8,padding:'8px 14px',fontSize:13,fontWeight:600,cursor:loading||!visible.length?'not-allowed':'pointer',opacity:loading||!visible.length?0.5:1}}>
              <Download size={14}/> Export
            </button>
            <button type="button" onClick={()=>fetchData(true)} disabled={refreshing}
              style={{display:'inline-flex',alignItems:'center',gap:6,background:tk.btnSec,color:tk.btnSecTxt,border:`1px solid ${tk.btnSecBdr}`,borderRadius:8,padding:'8px 14px',fontSize:13,fontWeight:500,cursor:'pointer'}}>
              <RefreshCw size={14} className={refreshing?'animate-spin':''}/> Refresh
            </button>
            <button type="button" onClick={()=>setShowModal(true)}
              onMouseEnter={e=>e.currentTarget.style.background=tk.btnPriH}
              onMouseLeave={e=>e.currentTarget.style.background=tk.btnPri}
              style={{display:'inline-flex',alignItems:'center',gap:6,background:tk.btnPri,color:tk.btnPriTxt,border:'none',borderRadius:8,padding:'8px 16px',fontSize:13,fontWeight:600,cursor:'pointer',transition:'background 0.15s'}}>
              <Plus size={14}/> New Campaign
            </button>
          </div>
        </div>

        {/* Summary */}
        <div className="mtc-stats">
          <Stat Icon={Megaphone}    label="Total"     value={stats.total}     accent="#6C63FF" loading={loading} tk={tk}/>
          <Stat Icon={Send}         label="Sent"      value={stats.sent}      accent="#3B82F6" loading={loading} tk={tk}/>
          <Stat Icon={CheckCircle2} label="Delivered" value={stats.delivered} accent="#10B981" loading={loading} tk={tk}/>
          <Stat Icon={XCircle}      label="Failed"    value={stats.failed}    accent="#EF4444" loading={loading} tk={tk}/>
        </div>

        {/* Filter bar */}
        <div className="mtc-filters">
          <div className="mtc-search">
            <Search size={15} color={tk.muted} style={{position:'absolute',left:11,top:'50%',transform:'translateY(-50%)'}}/>
            <input value={q} onChange={e=>setQ(e.target.value)} placeholder="Search campaigns…"
              style={{width:'100%',boxSizing:'border-box',background:tk.input,color:tk.text,border:`1px solid ${tk.border}`,borderRadius:8,padding:'9px 12px 9px 34px',fontSize:13,outline:'none'}}/>
          </div>
          <div style={{display:'flex',alignItems:'center',gap:6,color:tk.muted,fontSize:12,fontWeight:600}}>
            <Calendar size={14}/>From
            <input type="date" value={dateFrom} max={dateTo||undefined} onChange={e=>setDateFrom(e.target.value)} style={dateStyle}/>
          </div>
          <div style={{display:'flex',alignItems:'center',gap:6,color:tk.muted,fontSize:12,fontWeight:600}}>
            To
            <input type="date" value={dateTo} min={dateFrom||undefined} onChange={e=>setDateTo(e.target.value)} style={dateStyle}/>
          </div>
          {anyFilter && (
            <button type="button" onClick={clearAll}
              style={{display:'inline-flex',alignItems:'center',gap:5,background:'transparent',color:tk.accentTxt,border:'none',fontSize:12,fontWeight:600,cursor:'pointer'}}>
              <ListFilter size={13}/> Clear filters
            </button>
          )}
        </div>

        {/* Channel tabs */}
        <div className="mtc-tabs">
          {visibleTabs.map(tab => {
            const active = activeTab===tab;
            return (
              <button key={tab} type="button" onClick={()=>setActiveTab(tab)}
                style={{display:'inline-flex',alignItems:'center',gap:6,padding:'7px 14px',borderRadius:9,fontSize:13,fontWeight:600,cursor:'pointer',
                  background:active?tk.accentBg:tk.card, color:active?tk.accentTxt:tk.sub, border:`1px solid ${active?tk.accent+'40':tk.border}`}}>
                {tab}
                <span style={{fontSize:10,fontWeight:700,padding:'1px 6px',borderRadius:20,background:active?tk.accent+'22':tk.chip,color:active?tk.accentTxt:tk.muted}}>{tabCounts[tab]||0}</span>
              </button>
            );
          })}
        </div>

        {/* Status filter (Completed / In Process / Scheduled / Pending) */}
        <div style={{fontSize:10,fontWeight:700,color:tk.muted,letterSpacing:'0.08em',textTransform:'uppercase',margin:'4px 0 8px'}}>Status</div>
        <div className="mtc-chips" style={{marginBottom:14}}>
          {STATUS_BUCKETS.map(s => {
            const active = statusF===s;
            return (
              <button key={s} type="button" onClick={()=>setStatusF(s)}
                style={{display:'inline-flex',alignItems:'center',gap:6,padding:'6px 12px',borderRadius:20,fontSize:12,fontWeight:600,cursor:'pointer',
                  background:active?tk.accentBg:tk.card, color:active?tk.accentTxt:tk.sub, border:`1px solid ${active?tk.accent+'40':tk.border}`}}>
                {s}<span style={{color:tk.muted,fontWeight:700}}>{statusCounts[s]||0}</span>
              </button>
            );
          })}
        </div>

        {/* Categories */}
        <div style={{fontSize:10,fontWeight:700,color:tk.muted,letterSpacing:'0.08em',textTransform:'uppercase',margin:'4px 0 8px'}}>Categories</div>
        <div className="mtc-chips" style={{marginBottom:14}}>
          {['All','Marketing','Transactional','General'].map(c => {
            const active = catF===c;
            const dot = c==='All'?tk.accent:CAT_COLOR[c];
            const cnt = c==='All'?base.length:(catCounts[c]||0);
            return (
              <button key={c} type="button" onClick={()=>setCatF(c)}
                style={{display:'inline-flex',alignItems:'center',gap:6,padding:'6px 12px',borderRadius:20,fontSize:12,fontWeight:600,cursor:'pointer',
                  background:active?tk.accentBg:tk.card, color:active?tk.accentTxt:tk.sub, border:`1px solid ${active?tk.accent+'40':tk.border}`}}>
                <span style={{width:7,height:7,borderRadius:'50%',background:dot}}/>{c}
                <span style={{color:tk.muted,fontWeight:700}}>{cnt}</span>
              </button>
            );
          })}
        </div>

        {/* Labels */}
        <div style={{fontSize:10,fontWeight:700,color:tk.muted,letterSpacing:'0.08em',textTransform:'uppercase',margin:'4px 0 8px'}}>Labels</div>
        <div className="mtc-chips" style={{marginBottom:18}}>
          <button type="button" onClick={()=>setLabelF('All')}
            style={{display:'inline-flex',alignItems:'center',gap:6,padding:'6px 12px',borderRadius:20,fontSize:12,fontWeight:600,cursor:'pointer',
              background:labelF==='All'?tk.accentBg:tk.card, color:labelF==='All'?tk.accentTxt:tk.sub, border:`1px solid ${labelF==='All'?tk.accent+'40':tk.border}`}}>
            All <span style={{color:tk.muted,fontWeight:700}}>{afterCat.length}</span>
          </button>
          {Object.keys(labelCounts).sort().map(l => {
            const active = labelF===l;
            return (
              <button key={l} type="button" onClick={()=>setLabelF(active?'All':l)}
                style={{display:'inline-flex',alignItems:'center',gap:6,padding:'6px 12px',borderRadius:20,fontSize:12,fontWeight:600,cursor:'pointer',
                  background:active?tk.accentBg:tk.card, color:active?tk.accentTxt:tk.sub, border:`1px solid ${active?tk.accent+'40':tk.border}`}}>
                <Tag size={11}/>{l}<span style={{color:tk.muted,fontWeight:700}}>{labelCounts[l]}</span>
              </button>
            );
          })}
          {Object.keys(labelCounts).length===0 &&
            <span style={{fontSize:12,color:tk.muted}}>No labels yet — add one from any campaign row.</span>}
        </div>

        {/* Results */}
        {loading ? (
          <div style={{background:tk.card,border:`1px solid ${tk.border}`,borderRadius:12,padding:'40px 0',textAlign:'center',color:tk.muted,fontSize:13}}>Loading…</div>
        ) : visible.length===0 ? (
          <div style={{background:tk.card,border:`1px solid ${tk.border}`,borderRadius:12,padding:'48px 16px',display:'flex',flexDirection:'column',alignItems:'center'}}>
            <Megaphone size={28} color={tk.muted} style={{marginBottom:10}}/>
            <div style={{fontSize:13,fontWeight:600,color:tk.text,marginBottom:4}}>No campaigns match</div>
            <div style={{fontSize:12,color:tk.muted}}>{anyFilter?'Try clearing some filters.':'Launch your first campaign to get started.'}</div>
          </div>
        ) : (
          <>
            {/* Desktop table */}
            <div className="mtc-desktop" style={{background:tk.card,border:`1px solid ${tk.border}`,borderRadius:12,overflowX:'auto'}}>
              <table className="mtc-tbl">
                <thead><tr>
                  <th style={th}>Campaign</th><th style={th}>Category</th><th style={th}>Labels</th>
                  <th style={th}>Channel</th><th style={th}>Status</th><th style={{...th,textAlign:'right'}}>Sent</th>
                  <th style={{...th,textAlign:'right'}}>Opened</th><th style={{...th,textAlign:'right'}}>Clicked</th>
                  <th style={{...th,textAlign:'right'}}>Scheduled</th>
                </tr></thead>
                <tbody>
                  {visible.map((r,i) => {
                    const cp = channelPill(r.channel), sp = statusPill(r.status);
                    return (
                      <tr key={r.id||i} onClick={()=>setSelectedId(r.id)}
                        onMouseEnter={e=>{e.currentTarget.style.background=tk.accentBg;}}
                        onMouseLeave={e=>{e.currentTarget.style.background=i%2?tk.row:'transparent';}}
                        style={{background:i%2?tk.row:'transparent',cursor:'pointer',transition:'background 0.12s'}}>
                        <td style={{...td,fontWeight:600}}>{(r.name||'').replace(/_/g,' ')}</td>
                        <td style={td}>
                          <span style={{display:'inline-flex',alignItems:'center',gap:5,fontSize:11,fontWeight:600,color:tk.sub}}>
                            <span style={{width:7,height:7,borderRadius:'50%',background:CAT_COLOR[r.category]}}/>{r.category}
                          </span>
                        </td>
                        <td style={{...td,minWidth:150}}><LabelCell row={r}/></td>
                        <td style={td}><span style={{background:cp.bg,color:cp.color,fontSize:10,fontWeight:700,padding:'2px 8px',borderRadius:20,textTransform:'capitalize'}}>{r.channel}</span></td>
                        <td style={td}><span style={{background:sp.bg,color:sp.color,fontSize:10,fontWeight:700,padding:'2px 8px',borderRadius:20,textTransform:'capitalize'}}>{(r.status||'').replace(/_/g,' ')}</span></td>
                        <td style={{...td,textAlign:'right',fontWeight:600}}>{r.sent}</td>
                        <td style={{...td,textAlign:'right',color:tk.sub}}>{r.opened}</td>
                        <td style={{...td,textAlign:'right',color:tk.sub}}>{r.clicked}</td>
                        <td style={{...td,textAlign:'right',color:tk.sub,whiteSpace:'nowrap',fontSize:11.5}}>{toIST(r.date)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Mobile cards */}
            <div className="mtc-mobile">
              {visible.map((r,i) => {
                const cp = channelPill(r.channel), sp = statusPill(r.status);
                return (
                  <div key={r.id||i} onClick={()=>setSelectedId(r.id)}
                    style={{background:tk.card,border:`1px solid ${tk.border}`,borderRadius:12,padding:'14px 15px',cursor:'pointer'}}>
                    <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',gap:8,marginBottom:8}}>
                      <div style={{fontSize:14,fontWeight:600,color:tk.text}}>{(r.name||'').replace(/_/g,' ')}</div>
                      <span style={{background:sp.bg,color:sp.color,fontSize:10,fontWeight:700,padding:'2px 8px',borderRadius:20,textTransform:'capitalize',whiteSpace:'nowrap'}}>{(r.status||'').replace(/_/g,' ')}</span>
                    </div>
                    <div style={{display:'flex',gap:8,flexWrap:'wrap',alignItems:'center',marginBottom:10}}>
                      <span style={{background:cp.bg,color:cp.color,fontSize:10,fontWeight:700,padding:'2px 8px',borderRadius:20,textTransform:'capitalize'}}>{r.channel}</span>
                      <span style={{display:'inline-flex',alignItems:'center',gap:5,fontSize:11,fontWeight:600,color:tk.sub}}>
                        <span style={{width:7,height:7,borderRadius:'50%',background:CAT_COLOR[r.category]}}/>{r.category}
                      </span>
                      <span style={{fontSize:11,color:tk.muted,marginLeft:'auto'}}>{toIST(r.date)}</span>
                    </div>
                    <div style={{display:'flex',gap:16,marginBottom:10}}>
                      {[['Sent',r.sent],['Opened',r.opened],['Clicked',r.clicked]].map(([k,v])=>(
                        <div key={k}><div style={{fontSize:15,fontWeight:700,color:tk.text}}>{v}</div><div style={{fontSize:10,color:tk.muted,textTransform:'uppercase',letterSpacing:'0.05em',fontWeight:700}}>{k}</div></div>
                      ))}
                    </div>
                    <LabelCell row={r}/>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>
    </>
  );
}